import { useCallback, useSyncExternalStore } from 'react';
import { AssetList, ClipAssetResponse, TextAssetResponse, TypeDe2Enum } from '@goldcast/api/content';
import { Location } from 'react-router-dom';
import { useAppContext } from '@/context/AppContext/AppContext';
import { core } from '@/stores/core';
import { Clip } from '@/player-config/src/types/clip-types';
import { orgUsageSummaryState } from '@/stores/orgUsageSummary';
import { getImportLinkType } from '@/Pages/VideoImportPage/utils';
import { TextAsset } from '@/Pages/PostsListPage/types';
import {
  TransitionLifeCycleType,
  TransitionMoveDirection,
  TransitionRotateDirection,
  TransitionType
} from '@/player-config/src/types/RemotionPlayerConfigTypes/Animations/Transitions/Transitions.types';

export default function useAnalytics() {
  const { analytics } = useAppContext();

  const isSharedPage = window.location.pathname.includes('shared');
  const isSocialPost = window.location.pathname.includes('social');
  const isBlogPost = window.location.pathname.includes('blog');
  const isRecordingPage = window.location.pathname.includes('recordings');

  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);

  return {
    trackRouteChange: useCallback(
      (previousLocation: Location | undefined, toLocation: Location) => {
        /**
         * Track route change
         */
        analytics.track('Route change', {
          from: previousLocation?.pathname,
          fromName: previousLocation?.pathname,
          to: toLocation.pathname,
          toName: toLocation.pathname
        });
      },
      [analytics]
    ),
    trackContentEdited: useCallback(
      ({
        asset,
        isSuccess,
        updateType
      }: {
        asset: TextAsset;
        isSuccess: boolean;
        updateType: 'TextChange' | 'RegenerateClip' | 'ReplaceClip';
      }) => {
        /**
         * Track content edited
         * id = ID of the text asset
         * title = Title of the text asset
         * isSuccess = Whether the operation was successful or not
         * updateType = Type of update operation (TextChange, RegenerateClip, ReplaceClip)
         * contentType = Type of the asset metadata
         * isCustomUpload = Whether the asset belongs to a custom upload
         * isSample = Whether the asset is a sample upload
         */
        analytics.track('ContentLabEvent', {
          name: 'ContentEdited',
          title: asset.title,
          id: asset.id,
          isSuccess,
          updateType,
          contentType: asset.asset_metadata.type,
          isCustomUpload: coreStore.content?.media_source_type === 'UPLOAD',
          isSample: coreStore.content?.is_sample_upload,
          videoLanguage: coreStore.content?.video_language,
          isFullLength: !!asset.asset_metadata.is_full_length
        });
      },
      [analytics, coreStore.content]
    ),
    trackContentCreated: useCallback(
      ({
        source,
        asset,
        state,
        count,
        assetType = 'CLIP',
        failureReason,
        isMultimodal = false,
        isPromptChanged,
        prompt
      }: {
        source: 'Autogenerate' | 'Manual' | 'Prompt' | 'Regenerate' | 'ClipSocialPost';
        asset?: AssetList | Clip | ClipAssetResponse | TextAsset;
        assetType?: string;
        state: 'Created' | 'Failed' | 'Started';
        count?: number;
        failureReason?: string;
        isMultimodal?: boolean;
        isPromptChanged?: boolean;
        prompt?: string;
      }) => {
        analytics.track('ContentLabEvent', {
          name: 'ContentCreated',
          source,
          state,
          isSuccess: state !== 'Failed',
          assetType: assetType,
          isCustomUpload: coreStore.content?.media_source_type === 'UPLOAD',
          failureReason,
          isPromptChanged,
          isRecordingPage,
          prompt,
          isMultimodal,
          ...(asset
            ? {
                contentId: asset.content?.id,
                contentTitle: asset.content?.title,
                id: asset.id,
                title: asset.title,
                projectId: asset.content?.project.id,
                isGeneratedFromSampleVideo: coreStore.content?.is_sample_upload
              }
            : {}),
          ...(count ? { count } : {})
        });
      },
      [analytics, coreStore.content, isRecordingPage]
    ),
    trackContentDuplicated: useCallback(
      ({
        sourceAssetId,
        assetId,
        asset,
        assetType = 'CLIP',
        state,
        failureReason
      }: {
        sourceAssetId: string;
        assetId?: string;
        asset?: ClipAssetResponse;
        assetType?: TypeDe2Enum;
        state: 'Duplicated' | 'Failed' | 'Started';
        failureReason?: string;
      }) => {
        analytics.track('ContentLabEvent', {
          name: 'ContentDuplicated',
          sourceAssetId,
          assetId,
          state,
          assetType: assetType,
          isCustomUpload: coreStore.content?.media_source_type === 'UPLOAD',
          failureReason,
          ...(asset ? { asset } : {})
        });
      },
      [analytics, coreStore.content]
    ),
    trackTextFetched: useCallback(
      ({ count }: { count: number }) => {
        analytics.track('ContentLabEvent', {
          name: 'FetchedTexts',
          count
        });
      },
      [analytics]
    ),
    /**
     * This event content lab usage for a given org
     */
    trackOrgUsage: useCallback(() => {
      const orgUsageSummaryStateSnapshot = orgUsageSummaryState.getSnapshot();
      analytics.track('ContentLabEvent', {
        name: 'OrgUsage',
        totalClipsCount: orgUsageSummaryStateSnapshot.usage.clips.used,
        totalTextSummariesCount: orgUsageSummaryStateSnapshot.usage.text.used,
        mediaUploadedCount: orgUsageSummaryStateSnapshot.usage.videoUpload.count
      });
    }, [analytics]),
    trackContentLabUsage: useCallback(
      ({
        asset,
        isPlayClip
      }: {
        asset?: AssetList | Clip;
        isPlayClip?: boolean;
        mediaUploadedCount?: number;
      } = {}) => {
        analytics.track('ContentLabEvent', {
          name: 'ContentLabUsage',
          isSample: coreStore.content?.is_sample_upload,
          assetId: asset?.id,
          assetTitle: asset?.title,
          assetType: (asset as AssetList)?.type !== undefined ? (asset as AssetList).type : 'CLIP',
          isPlayClip,
          isSharedPage,
          isSocialPost,
          isBlogPost,
          isOpened: !!asset
        });
      },
      [analytics, coreStore.content?.is_sample_upload, isSharedPage, isSocialPost, isBlogPost]
    ),
    trackFetchedClips: useCallback(
      ({ count }: { count: number }) => {
        analytics.track('ContentLabEvent', {
          name: 'FetchedClips',
          count
        });
      },
      [analytics]
    ),
    trackTextCopiedToClipboard: useCallback(
      (post: TextAsset) => {
        analytics.track('ContentLabEvent', {
          name: 'TextCopiedToClipboard',
          isCustomUpload: coreStore.content?.media_source_type === 'UPLOAD',
          contentType: post.asset_metadata.type,
          id: post.id
        });
      },
      [analytics, coreStore.content?.media_source_type]
    ),
    trackTextEmailed: useCallback(() => {
      analytics.track('ContentLabEvent', {
        name: 'TextEmailed',
        isCustomUpload: coreStore.content?.media_source_type === 'UPLOAD'
      });
    }, [analytics, coreStore.content]),
    trackUsageCapLimitReached: useCallback(
      (entity: 'CLIPS' | 'VIDEO_UPLOAD') => {
        analytics.track('ContentLabEvent', {
          name: 'UsageCapLimitReached',
          entity,
          timestamp: new Date().toISOString()
        });
      },
      [analytics]
    ),
    trackDownloadContent: useCallback(
      ({
        clip,
        textAsset,
        assetType,
        state,
        isUpscaled = false,
        isSplitsEnabled = false
      }: {
        clip?;
        textAsset?: TextAsset | null;
        assetType: TypeDe2Enum;
        state: 'Autodownload' | 'ButtonClick' | 'ToastButtonClick' | 'Failed';
        isUpscaled?: boolean;
        isSplitsEnabled?: boolean;
      }) => {
        const playerConfig = clip?.asset_metadata?.player_config;
        const tracks: any[] = playerConfig?.playerConfig?.tracks ?? [];
        const elementsTrack = tracks.find((t: any) => t.type === 'ELEMENTS');
        const elements: any[] = elementsTrack?.elements ?? [];
        const segmentsTrack = tracks.find((t: any) => t.type === 'SEGMENTS');
        const segments: any[] = segmentsTrack?.segments ?? [];

        const hasDeletes = Object.keys(clip?.asset_metadata?.deletes ?? {}).length > 0;
        const hasTrim = segments.some(
          (seg: any) =>
            seg.type === 'COMPOSITE' &&
            (seg.currentBounds?.fromFrame !== seg.maxBounds?.fromFrame ||
              seg.currentBounds?.toFrame !== seg.maxBounds?.toFrame)
        );
        const hasVideoOverlay = elements.some((el: any) => el.type === 'VIDEO' && !el.isOriginalVideo);
        const hasImageOverlay = elements.some((el: any) => el.type === 'IMAGE');
        const hasAudioOverlay = elements.some((el: any) => el.type === 'AUDIO');
        const hasMultipleScenes = clip?.asset_metadata?.scenes?.length > 1;
        const hasManualRepositioning = !!clip?.asset_metadata?.scenes?.some(
          (scene: any) => Object.keys(scene?.details?.video_crop_position_map ?? {}).length > 0
        );
        const hasCaptions = !!clip?.asset_metadata?.subtitle;

        analytics.track('ContentLabEvent', {
          name: 'DownloadContent',
          assetType,
          isSuccessful: state !== 'Failed',
          isUpscaled,
          isSocialPost,
          isBlogPost,
          isRecordingPage,
          state,
          url: window.location.hostname + window.location.pathname,
          ...(!clip
            ? {}
            : {
                clipId: clip.id,
                layout: clip.asset_metadata.layout,
                title: clip.title,
                isCustomUpload: clip.content.media_source_type === 'UPLOAD',
                is_sample_upload: clip.content.is_sample_upload,
                generatedBy: clip.asset_metadata.source,
                selectedLanguage: clip?.asset_metadata?.selected_lang,
                originalLanguage: clip?.asset_metadata?.original_lang,
                isSplitsEnabled,
                hasDeletes,
                hasTrim,
                hasVideoOverlay,
                hasImageOverlay,
                hasAudioOverlay,
                hasScenes: hasMultipleScenes,
                hasManualRepositioning,
                hasCaptions
              }),
          ...(!textAsset
            ? {}
            : {
                title: textAsset.title,
                isMultiModal: textAsset.content?.multi_modal_enabled,
                contentId: textAsset.content?.id,
                contentType: textAsset?.asset_metadata?.type
              })
        });
      },
      [analytics, isSocialPost, isBlogPost, isRecordingPage]
    ),
    trackVideoUpload: useCallback(
      ({
        uploadType,
        failureReason,
        url,
        status
      }: {
        uploadType: 'Upload' | 'Import';
        status: 'Completed' | 'Failed' | 'Started' | 'Disabled';
        failureReason?: string;
        url?: string;
      }) => {
        analytics.track('ContentLabEvent', {
          name: 'VideoUpload',
          uploaded: 1,
          status,
          count: 1,
          currentPage: window.location.pathname,
          uploadType,
          urlType: url ? getImportLinkType(url) : undefined,
          isSuccess: status === 'Completed',
          failureReason,
          url
        });
      },
      [analytics]
    ),
    trackContentShare: useCallback(
      ({
        asset,
        contentType,
        source,
        shareType,
        isSuccess = true
      }: {
        asset: TextAssetResponse | Clip | TextAsset | null;
        contentType: string;
        source: 'EditorPage' | 'SharedPage';
        shareType: 'CopyURL' | 'OpenPopup';
        isSuccess?: boolean;
      }) => {
        analytics.track('ContentLabEvent', {
          name: 'ContentShare',
          shareType,
          contentType,
          source,
          isSuccess,
          title: asset?.title,
          id: asset?.id,
          isRecordingPage
        });
      },
      [analytics, isRecordingPage]
    ),
    trackAiSearch: useCallback(
      ({ term, contentType }: { term: string; contentType: string }) => {
        analytics.track('ContentLabEvent', {
          name: 'AiSearchInitiated',
          term,
          contentType
        });
      },
      [analytics]
    ),
    trackAiSearchFailure: useCallback(
      (error: string, prompt: string) => {
        analytics.track('ContentLabEvent', {
          name: 'AiSearchFailure',
          error,
          prompt
        });
      },
      [analytics]
    ),
    trackVideoImportCustomizations: useCallback(
      (action: 'ColorChange' | 'FontChange' | 'TemplateSelect') => {
        analytics.track('ContentLabEvent', {
          name: 'VideoImportCustomizations',
          action
        });
      },
      [analytics]
    ),
    trackVoiceProfileLimitReached: useCallback(() => {
      analytics.track('ContentLabEvent', {
        name: 'VoiceProfileLimitReachedCTA'
      });
    }, [analytics]),
    trackVideoPublished: useCallback(
      ({ id, isSuccess }: { id: string; isSuccess: boolean }) => {
        analytics.track('ContentLabEvent', {
          name: 'VideoPublished',
          id,
          isSuccess
        });
      },
      [analytics]
    ),
    trackRecordingStudioClick: useCallback(() => {
      analytics.track('ContentLabEvent', {
        name: 'RecordingStudioClicked'
      });
    }, [analytics]),
    trackTranscriptionRestricted: useCallback(() => {
      analytics.track('ContentLabEvent', {
        name: 'MultiLanguageTranscriptionRestricted'
      });
    }, [analytics]),
    trackCaptionTranslationRestricted: useCallback(() => {
      analytics.track('ContentLabEvent', {
        name: 'CaptionTranslationRestricted'
      });
    }, [analytics]),
    trackPlayerBuffering: useCallback(
      ({
        state,
        clipId,
        bufferStateId,
        useNewVideoTag
      }: {
        state: 'Buffering' | 'Loaded';
        clipId: string;
        bufferStateId: string;
        useNewVideoTag: boolean;
      }) => {
        analytics.track('PlayerEvent', {
          bufferStateId,
          name: 'BufferingState',
          state,
          clipId,
          useNewVideoTag,
          eventTime: Date.now()
        });
      },
      [analytics]
    ),
    trackPlayerSeek: useCallback(
      ({ clipId, seekTime }: { clipId: string; seekTime: number }) => {
        analytics.track('PlayerEvent', {
          name: 'Seek',
          clipId,
          seekTime,
          eventTime: Date.now()
        });
      },
      [analytics]
    ),
    trackPlayerPlay: useCallback(
      ({ clipId, currentTime }: { clipId: string; currentTime: number }) => {
        analytics.track('PlayerEvent', {
          name: 'Play',
          clipId,
          currentTime,
          eventTime: Date.now()
        });
      },
      [analytics]
    ),
    trackPlayerPause: useCallback(
      ({ clipId, currentTime }: { clipId: string; currentTime: number }) => {
        analytics.track('PlayerEvent', {
          name: 'Pause',
          clipId,
          currentTime,
          eventTime: Date.now()
        });
      },
      [analytics]
    ),
    trackTranscriptLoadTime: useCallback(
      ({
        eventId,
        broadcastId,
        context,
        loadTime
      }: {
        eventId: string;
        broadcastId: string;
        context: string;
        loadTime: number;
      }) => {
        analytics.track('ContentLabEvent', {
          name: 'TranscriptLoadTime',
          eventId,
          broadcastId,
          context,
          loadTime
        });
      },
      [analytics]
    ),
    trackClipLoadTime: useCallback(
      ({ clipId, loadTime, context }: { clipId: string; loadTime: number; context: string }) => {
        analytics.track('ContentLabEvent', {
          name: 'ClipLoadTime',
          clipId,
          loadTime,
          context
        });
      },
      [analytics]
    ),
    trackClipRecommendationEvent: useCallback(
      (eventName: string, properties: Record<string, any>) => {
        analytics.track('ClipCuratorEvent', {
          name: eventName,
          ...properties
        });
      },
      [analytics]
    ),
    trackEventMigrationTriggered: useCallback(
      (orgId: string, source: string) => {
        analytics.track('ContentLabEvent', {
          name: 'EventMigrationTriggered',
          orgId,
          source
        });
      },
      [analytics]
    ),
    trackEventMigrationPostponed: useCallback(
      (orgId: string, source: string) => {
        analytics.track('ContentLabEvent', {
          name: 'EventMigrationPostponed',
          orgId,
          source
        });
      },
      [analytics]
    ),
    trackEventMigrationDismissed: useCallback(
      (orgId: string, source: string) => {
        analytics.track('ContentLabEvent', {
          name: 'EventMigrationDismissed',
          orgId,
          source
        });
      },
      [analytics]
    ),
    trackUsingOldPlayerPostSunset: useCallback(
      ({ properties, type }: { properties: object; type: string }) => {
        window.logger?.error('USING_OLD_PLAYER_POST_SUNSET', {
          properties,
          type
        });
        analytics.track('ContentLabEvent', {
          name: 'UsingOldPlayerPostSunset',
          properties,
          type
        });
      },
      [analytics]
    ),
    trackUsingOldClipListPage: useCallback(() => {
      window.logger?.error('USING_OLD_CLIP_LIST_PAGE', {});
      analytics.track('ContentLabEvent', {
        name: 'UsingOldClipListPage'
      });
    }, [analytics]),
    trackSharedPageWithoutRemotion: useCallback(
      ({ properties }: { properties: object }) => {
        analytics.track('ContentLabEvent', {
          name: 'SharedPageWithoutRemotion',
          properties
        });
      },
      [analytics]
    ),
    trackTransitionButtonClicked: useCallback(
      ({ clipId, assetType }: { clipId: string; assetType: 'IMAGE' | 'VIDEO' }) => {
        analytics.track('ContentLabEvent', { name: 'TransitionButtonClicked', clipId, assetType });
      },
      [analytics]
    ),
    trackTransitionLifeCycleSelect: useCallback(
      ({
        clipId,
        assetType,
        lifeCycleType
      }: {
        clipId: string;
        assetType: 'IMAGE' | 'VIDEO';
        lifeCycleType: TransitionLifeCycleType;
      }) => {
        analytics.track('ContentLabEvent', { name: 'TransitionLifeCycleSelect', clipId, assetType, lifeCycleType });
      },
      [analytics]
    ),
    trackManualRepositioningPopupOpen: useCallback(
      ({ clipId, fromRemotionPlayer }: { clipId: string; fromRemotionPlayer: boolean }) => {
        analytics.track('ContentLabEvent', {
          name: 'ManualRepositioningPopupOpen',
          clipId,
          fromRemotionPlayer
        });
      },
      [analytics]
    ),
    trackTransitionTypeSelect: useCallback(
      ({
        clipId,
        assetType,
        mode,
        lifeCycleType,
        transitionType
      }: {
        clipId: string;
        assetType: 'IMAGE' | 'VIDEO';
        mode: 'INSERT' | 'REMOVE' | 'UPDATE';
        lifeCycleType: TransitionLifeCycleType;
        transitionType: TransitionType | null;
      }) => {
        analytics.track('ContentLabEvent', {
          name: 'TransitionTypeSelect',
          clipId,
          assetType,
          mode,
          lifeCycleType,
          transitionType
        });
      },
      [analytics]
    ),

    trackTransitionMoveDirectionSelect: useCallback(
      ({
        clipId,
        assetType,
        lifeCycleType,
        direction
      }: {
        clipId: string;
        assetType: 'IMAGE' | 'VIDEO';
        lifeCycleType: TransitionLifeCycleType;
        direction: TransitionMoveDirection;
      }) => {
        analytics.track('ContentLabEvent', {
          name: 'TransitionMoveDirectionSelect',
          clipId,
          assetType,
          lifeCycleType,
          direction
        });
      },
      [analytics]
    ),
    trackTransitionRotateDirectionSelect: useCallback(
      ({
        clipId,
        assetType,
        lifeCycleType,
        direction
      }: {
        clipId: string;
        assetType: 'IMAGE' | 'VIDEO';
        lifeCycleType: TransitionLifeCycleType;
        direction: TransitionRotateDirection;
      }) => {
        analytics.track('ContentLabEvent', {
          name: 'TransitionRotateDirectionSelect',
          clipId,
          assetType,
          lifeCycleType,
          direction
        });
      },
      [analytics]
    ),
    trackTransitionDurationUpdateSource: useCallback(
      ({ source }: { source: string }) => {
        analytics.track('ContentLabEvent', {
          name: 'TransitionDurationUpdateSource',
          source
        });
      },
      [analytics]
    ),
    trackTransitionDurationUpdate: useCallback(
      ({
        clipId,
        assetType,
        transitionLifeCycle,
        transitionType,
        duration
      }: {
        clipId: string;
        assetType: 'IMAGE' | 'VIDEO';
        transitionLifeCycle: TransitionLifeCycleType;
        transitionType: TransitionType;
        duration: number;
      }) => {
        analytics.track('ContentLabEvent', {
          name: 'TransitionDurationUpdate',
          clipId,
          assetType,
          transitionLifeCycle,
          transitionType,
          duration
        });
      },
      [analytics]
    ),
    trackManualRepositioningApplied: useCallback(
      ({ clipId, fromRemotionPlayer }: { clipId: string; fromRemotionPlayer: boolean }) => {
        analytics.track('ContentLabEvent', {
          name: 'ManualRepositioningApplied',
          clipId,
          fromRemotionPlayer
        });
      },
      [analytics]
    ),
    trackClipOpenedWithAsdCompleted: useCallback(
      ({ clipId }: { clipId: string }) => {
        analytics.track('ContentLabEvent', { name: 'ClipOpenedWithAsdCompleted', clipId });
      },
      [analytics]
    ),
    trackManualSpeakerMappingDone: useCallback(
      ({ clipId }: { clipId: string }) => {
        analytics.track('ContentLabEvent', { name: 'ManualSpeakerMappingDone', clipId });
      },
      [analytics]
    ),
    trackAudioControlButtonClick: useCallback(
      ({ clipId, isOpen, clipType }: { clipId: string; isOpen: boolean; clipType: string }) => {
        analytics.track('ContentLabEvent', { name: 'AudioControlButtonClick', clipId, isOpen, clipType });
      },
      [analytics]
    ),
    trackVolumeChange: useCallback(
      (props: {
        clipId: string;
        clipType: string;
        source: 'SLIDER' | 'INPUT' | 'MUTE';
        volume: number;
        muted: boolean;
        controlType: string;
        label: string;
      }) => {
        analytics.track('ContentLabEvent', { name: 'VolumeChange', ...props });
      },
      [analytics]
    ),
    trackTemplateSelected: useCallback(
      ({
        templateId,
        templateName,
        templateType,
        layout
      }: {
        templateId: string | undefined;
        templateName: string;
        templateType: 'Preset' | 'My Templates';
        layout: string | undefined;
      }) => {
        analytics.track('ContentLabEvent', {
          name: 'TemplateSelected',
          templateId,
          templateName,
          templateType,
          layout
        });
      },
      [analytics]
    ),
    trackTemplateCreated: useCallback(
      ({
        templateId,
        templateName,
        layout
      }: {
        templateId?: string;
        templateName: string;
        layout: string | undefined;
      }) => {
        analytics.track('ContentLabEvent', {
          name: 'TemplateCreated',
          templateId,
          templateName,
          layout
        });
      },
      [analytics]
    ),
    trackTemplateApplied: useCallback(
      ({
        templateId,
        templateName,
        templateSource,
        clipId,
        layout
      }: {
        templateId?: string;
        templateName: string;
        templateSource: 'user' | 'system';
        clipId: string;
        layout: string | undefined;
      }) => {
        analytics.track('ContentLabEvent', {
          name: 'TemplateApplied',
          templateId,
          templateName,
          templateSource,
          clipId,
          layout
        });
      },
      [analytics]
    ),
    trackUndo: useCallback(
      ({ clipId, undoneAction }: { clipId: string; undoneAction: string | undefined }) => {
        analytics.track('ContentLabEvent', {
          name: 'Undo',
          clipId,
          undoneAction
        });
      },
      [analytics]
    )
  };
}
